import { API_BASE } from '@/shared/services/api';
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/shared/contexts/AuthContext';
import { toast } from 'sonner';
import { cn } from '@/shared/utils';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/shared/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/shared/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { ScrollArea, ScrollBar } from '@/shared/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { Label } from '@/shared/components/ui/label';
import { Textarea } from '@/shared/components/ui/textarea';
import { Switch } from '@/shared/components/ui/switch';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui/avatar';
import {
  Shield, ShieldCheck, ShieldX, ShieldQuestion, Users, UserPlus, UserMinus,
  Plus, Trash2, Edit, RefreshCw, Search, Filter, Eye, EyeOff,
  Hammer, UserCog, Car, Warehouse, Check, X, Minus, ChevronDown, ChevronRight, ChevronsUpDown,
  LayoutDashboard, Package, Truck, Settings, ScanLine, Box, MapPin, Thermometer,
  Receipt, BookOpen, Target, MessageSquare, FileText, Activity, AlertTriangle,
  Gauge, Bell, History, Upload, Download, PenTool, Camera, KeyRound, RotateCcw,
  Play, CheckCircle, PackageCheck, PackageX, ArrowDownToLine, MoveDown, ShoppingCart,
  PackageSearch, BoxSelect, Send, Route, UserPlus as UserPlusIcon, Building2, BarChart3,
  FileCheck, ClipboardList, Pencil, Cog, FilePlus, Wallet, Building, Crosshair, Mail,
  MessageCircle, Clock, Calendar, CalendarDays, CalendarPlus, DollarSign, Lock, Inbox,
  CalendarCheck, CalendarClock, Store, LayoutGrid, Kanban
} from 'lucide-react';

const API_URL = API_BASE;

// Icon mapping for capabilities
const ICON_MAP = {
  LayoutDashboard, Package, Truck, Settings, ScanLine, Box, MapPin, Thermometer,
  Receipt, BookOpen, Target, MessageSquare, FileText, Activity, AlertTriangle,
  Gauge, Bell, History, Upload, Download, PenTool, Camera, KeyRound, RotateCcw,
  Play, CheckCircle, PackageCheck, PackageX, ArrowDownToLine, MoveDown, ShoppingCart,
  PackageSearch, BoxSelect, Send, Route, UserPlus: UserPlusIcon, Building2, BarChart3,
  Shield, ShieldCheck, Users, UserCog, Car, Warehouse, Eye, FileCheck, ClipboardList,
  Pencil, Cog, FilePlus, Wallet, Building, Crosshair, Mail, MessageCircle,
  Clock, Calendar, CalendarDays, CalendarPlus, DollarSign, Lock, Inbox, CalendarCheck,
  CalendarClock, Store, LayoutGrid, Kanban
};

// Module display names and colors
const MODULE_CONFIG = {
  wms: { name: 'WMS', color: '#2563eb', icon: Warehouse },
  ezparcel: { name: 'EzParcel', color: '#dc2626', icon: Truck },
  driver: { name: 'Driver Mobile', color: '#f59e0b', icon: Car },
  mobile: { name: 'Mobile WMS', color: '#06b6d4', icon: ScanLine },
  trails: { name: 'Trails', color: '#8b5cf6', icon: Activity },
  coldchain: { name: 'Cold Chain', color: '#14b8a6', icon: Thermometer },
  geofencing: { name: 'Geofencing', color: '#22c55e', icon: MapPin },
  admin: { name: 'Administration', color: '#ef4444', icon: Shield },
  finance: { name: 'Finance', color: '#16a34a', icon: Receipt },
  procurement: { name: 'Procurement', color: '#4f46e5', icon: ShoppingCart },
  crm: { name: 'CRM', color: '#ec4899', icon: Users },
  communications: { name: 'Communications', color: '#8b5cf6', icon: MessageSquare },
  oig: { name: 'OIG', color: '#06b6d4', icon: FileText },
  hr: { name: 'Human Resources', color: '#7c3aed', icon: Users },
  accounting: { name: 'Accounting', color: '#059669', icon: BookOpen },
  eztask: { name: 'EzTask', color: '#f59e0b', icon: ClipboardList },
  calendar: { name: 'Calendar', color: '#3b82f6', icon: LayoutDashboard },
  ecommerce: { name: 'Ecommerce', color: '#10b981', icon: ShoppingCart },
  cns: { name: 'Social Media', color: '#ec4899', icon: MessageCircle },
  reports: { name: 'Report Builder', color: '#3b82f6', icon: BarChart3 }
};

// Template icon mapping
const TEMPLATE_ICON_MAP = {
  Eye, Hammer, UserCog, ShieldCheck, ShieldX, Settings, Shield,
  Warehouse, Truck, DollarSign: Receipt
};

// Default templates (fallback)
const DEFAULT_TEMPLATES = [
  { value: 'viewer', label: 'Viewer', desc: 'Read-only access', icon: Eye, color: '#64748b' },
  { value: 'operator', label: 'Operator', desc: 'Execute operations', icon: Hammer, color: '#22c55e' },
  { value: 'manager', label: 'Manager', desc: 'Full module access', icon: UserCog, color: '#3b82f6' },
  { value: 'administrator', label: 'Administrator', desc: 'Full system access', icon: ShieldCheck, color: '#8b5cf6' },
  { value: 'denied', label: 'Denied', desc: 'No access', icon: ShieldX, color: '#ef4444' },
  { value: 'custom', label: 'Custom', desc: 'Custom permissions', icon: Settings, color: '#6366f1' }
];

const sortByText = (items, getText) => (
  [...(items || [])].sort((a, b) => (
    String(getText(a) || '').localeCompare(String(getText(b) || ''), undefined, {
      numeric: true,
      sensitivity: 'base'
    })
  ))
);

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin' },
  { value: 'manager', label: 'Manager' },
  { value: 'operator', label: 'Operator' },
  { value: 'viewer', label: 'Viewer' }
];

const CompactStatCard = ({ title, value, helper, icon: Icon, borderClass, iconBgClass, iconClass }) => (
  <Card className={cn('card-elevated border-l-4 min-h-[84px] transition-all duration-200 hover:shadow-lg', borderClass)}>
    <CardContent className="px-5 py-4">
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-normal text-muted-foreground">{title}</p>
          <div className="mt-1 flex items-baseline gap-2">
            <p className="text-2xl font-bold leading-none text-foreground">{value}</p>
            {helper && <p className="truncate text-xs text-muted-foreground">{helper}</p>}
          </div>
        </div>
        <div className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-lg', iconBgClass)}>
          <Icon className={cn('h-5 w-5', iconClass)} />
        </div>
      </div>
    </CardContent>
  </Card>
);

// Permission state component - 3-state toggle
const PermissionCell = ({ state, onToggle, isReadOnly, capabilityName, isSensitive, isUngrantable }) => {
  const getNextState = (current) => {
    if (current === 'allow') return 'deny';
    if (current === 'deny') return 'not_set';
    return 'allow';
  };

  const stateConfig = {
    allow: { icon: Check, color: 'bg-emerald-500 text-white', label: 'Allowed' },
    deny: { icon: X, color: 'bg-red-500 text-white', label: 'Denied' },
    not_set: { icon: Minus, color: 'bg-slate-300 dark:bg-slate-600 text-slate-600 dark:text-slate-300', label: 'Not Set' }
  };

  const config = stateConfig[state] || stateConfig.not_set;
  const Icon = config.icon;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={() => !isReadOnly && onToggle(getNextState(state))}
            disabled={isReadOnly}
            className={cn(
              'w-8 h-8 rounded flex items-center justify-center transition-all duration-150',
              config.color,
              isReadOnly ? 'opacity-50 cursor-not-allowed' : 'hover:scale-110 cursor-pointer',
              isSensitive && !isUngrantable && 'ring-2 ring-amber-400 ring-offset-1',
              isUngrantable && 'ring-2 ring-slate-400 ring-offset-1 ring-dashed'
            )}
            data-testid={`permission-cell-${capabilityName}`}
          >
            <Icon className="h-4 w-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top">
          <p className="font-medium">{capabilityName}</p>
          <p className="text-xs text-muted-foreground">{config.label}</p>
          {isSensitive && <p className="text-xs text-amber-500">⚠️ Sensitive Permission</p>}
          {isUngrantable && <p className="text-xs text-slate-400">🔒 Cannot grant (outside your scope)</p>}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Profile row in the matrix
const ProfileRow = ({ profile, columns, onPermissionChange, onEdit, onDelete, expandedModules, isCapabilityGrantable }) => {
  const Icon = ICON_MAP[profile.icon] || Shield;
  
  return (
    <div className="flex items-center border-b border-border hover:bg-muted/30 transition-colors">
      {/* Profile info - sticky left column */}
      <div className="sticky left-0 z-10 bg-card flex items-center gap-3 p-3 min-w-[220px] w-[220px] border-r border-border">
        <div 
          className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: profile.color }}
        >
          <Icon className="h-4 w-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">{profile.name}</p>
          <div className="flex items-center gap-1.5">
            <Badge variant="outline" className="text-[10px] px-1.5 py-0">
              {profile.template}
            </Badge>
            {profile.is_system && (
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">System</Badge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit(profile)}>
            <Edit className="h-3.5 w-3.5" />
          </Button>
          {!profile.is_system && (
            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => onDelete(profile)}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
      
      {/* Permission cells */}
      <div className="flex">
        {columns.map((cap, idx) => {
          const permission = profile.permissions?.find(p => p.capability_id === cap.id);
          const state = permission?.state || 'not_set';
          const isModuleStart = cap.is_module_start;
          const canGrant = isCapabilityGrantable ? isCapabilityGrantable(cap.code) : true;
          
          return (
            <div 
              key={cap.id} 
              className={cn(
                'p-1.5 flex items-center justify-center',
                isModuleStart && 'border-l-2 border-l-slate-400/30',
                !canGrant && 'opacity-40 bg-muted/30'
              )}
              style={{ minWidth: '44px' }}
            >
              <PermissionCell
                state={state}
                onToggle={(newState) => onPermissionChange(profile.id, cap.id, newState)}
                isReadOnly={(profile.is_system && profile.template === 'administrator') || !canGrant}
                capabilityName={cap.name}
                isSensitive={cap.is_sensitive}
                isUngrantable={!canGrant}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Column header for a capability
const CapabilityHeader = ({ capability, moduleColor }) => {
  const Icon = ICON_MAP[capability.icon] || Settings;
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div 
            className={cn(
              'flex flex-col items-center justify-end p-2 cursor-help',
              capability.is_module_start && 'border-l-2'
            )}
            style={{ 
              minWidth: '44px',
              borderColor: capability.is_module_start ? moduleColor : 'transparent'
            }}
          >
            <div 
              className="w-7 h-7 rounded flex items-center justify-center mb-1"
              style={{ backgroundColor: `${moduleColor}20` }}
            >
              <Icon className="h-4 w-4" style={{ color: moduleColor }} />
            </div>
            <span className="text-[10px] text-muted-foreground text-center leading-tight max-w-[40px] truncate">
              {capability.name.split(' ').slice(-1)[0]}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p className="font-medium">{capability.name}</p>
          <p className="text-xs text-muted-foreground">{capability.code}</p>
          {capability.is_sensitive && <p className="text-xs text-amber-500">⚠️ Sensitive</p>}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Module group header
const ModuleGroupHeader = ({ module, capabilities, isExpanded, onToggle }) => {
  const config = MODULE_CONFIG[module] || { name: module, color: '#6366f1', icon: Box };
  const Icon = config.icon;
  
  return (
    <button
      onClick={onToggle}
      className="flex items-center gap-2 px-3 py-2 hover:bg-muted/50 rounded-lg transition-colors w-full"
    >
      <div 
        className="w-6 h-6 rounded flex items-center justify-center"
        style={{ backgroundColor: config.color }}
      >
        <Icon className="h-3.5 w-3.5 text-white" />
      </div>
      <span className="font-medium text-sm">{config.name}</span>
      <Badge variant="outline" className="ml-auto text-xs">{capabilities.length}</Badge>
      {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
    </button>
  );
};

// Create/Edit Profile Dialog
const ProfileDialog = ({ profile, isOpen, onClose, onSave }) => {
  const [form, setForm] = useState({
    name: '',
    description: '',
    template: 'custom',
    color: '#6366f1',
    icon: 'shield'
  });
  
  const [templates, setTemplates] = useState(DEFAULT_TEMPLATES);
  
  // Fetch templates from API
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const API_URL = API_BASE;
        const response = await fetch(`${API_URL}/api/reference/permission-templates`);
        if (response.ok) {
          const data = await response.json();
          const mappedTemplates = data.map(t => ({
            value: t.code.toLowerCase(),
            label: t.label,
            desc: t.description || t.label,
            icon: TEMPLATE_ICON_MAP[t.icon] || Shield,
            color: t.color || '#6366f1'
          }));
          if (mappedTemplates.length > 0) {
            setTemplates(mappedTemplates);
          }
        }
      } catch (error) {
        console.error('Failed to fetch templates:', error);
      }
    };
    fetchTemplates();
  }, []);

  useEffect(() => {
    if (profile) {
      setForm({
        name: profile.name || '',
        description: profile.description || '',
        template: profile.template || 'custom',
        color: profile.color || '#6366f1',
        icon: profile.icon || 'shield'
      });
    } else {
      setForm({ name: '', description: '', template: 'custom', color: '#6366f1', icon: 'shield' });
    }
  }, [profile, isOpen]);

  const handleSubmit = () => {
    if (!form.name.trim()) {
      toast.error('Profile name is required');
      return;
    }
    onSave(form);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{profile ? 'Edit Profile' : 'Create New Profile'}</DialogTitle>
          <DialogDescription>
            {profile ? 'Modify the profile settings below.' : 'Create a new access profile with custom permissions.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="name">Profile Name</Label>
            <Input
              id="name"
              value={form.name}
              onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g., Warehouse Supervisor"
              data-testid="profile-name-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={form.description}
              onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Describe the role and responsibilities..."
              rows={2}
            />
          </div>
          <div className="space-y-2">
            <Label>Template</Label>
            <Select value={form.template} onValueChange={(v) => setForm(f => ({ ...f, template: v }))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {sortByText(templates, template => template.label).map(t => {
                  const TIcon = t.icon;
                  return (
                    <SelectItem key={t.value} value={t.value}>
                      <div className="flex items-center gap-2">
                        <TIcon className="h-4 w-4" style={{ color: t.color }} />
                        <span>{t.label}</span>
                        <span className="text-xs text-muted-foreground">- {t.desc}</span>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Color</Label>
            <div className="flex gap-2">
              {['#6366f1', '#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#14b8a6', '#ec4899'].map(color => (
                <button
                  key={color}
                  onClick={() => setForm(f => ({ ...f, color }))}
                  className={cn(
                    'w-8 h-8 rounded-full transition-transform',
                    form.color === color && 'ring-2 ring-offset-2 ring-slate-500 scale-110'
                  )}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} data-testid="save-profile-btn">
            {profile ? 'Save Changes' : 'Create Profile'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// User Assignment Dialog
const UserAssignmentDialog = ({
  profile,
  users,
  isOpen,
  onClose,
  onAssign,
  onUnassign,
  onEditUser,
  onDeleteUser,
  onNewUser
}) => {
  const [selectedUser, setSelectedUser] = useState('');
  const [search, setSearch] = useState('');
  const [scope, setScope] = useState('assigned');
  const [pageSize, setPageSize] = useState('10');
  const [page, setPage] = useState(1);
  const [pendingRemoval, setPendingRemoval] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const assignedUserIds = useMemo(
    () => profile?.users?.map(u => u.user_id || u.id) || [],
    [profile?.users]
  );
  const assignedUserIdSet = useMemo(() => new Set(assignedUserIds), [assignedUserIds]);
  const assignmentByUserId = useMemo(() => {
    const next = new Map();
    (profile?.users || []).forEach(user => next.set(user.user_id || user.id, user));
    return next;
  }, [profile?.users]);

  const allKnownUsers = useMemo(() => {
    const byId = new Map();
    (users || []).forEach(user => {
      if (user?.id) byId.set(user.id, { ...user, id: user.id, user_id: user.id });
    });
    (profile?.users || []).forEach(assigned => {
      const id = assigned.user_id || assigned.id;
      if (!id) return;
      const existing = byId.get(id);
      byId.set(id, { ...(assigned || {}), ...(existing || {}), id, user_id: id });
    });
    return sortByText(Array.from(byId.values()), user => {
      const name = composeUserFullName(user) || user.full_name || user.username || user.email;
      return `${name || ''} ${user.email || ''}`;
    });
  }, [profile?.users, users]);

  const availableUsers = useMemo(
    () => sortByText(
      allKnownUsers.filter(u => !assignedUserIdSet.has(u.id)),
      user => `${user.full_name || ''} ${user.username || ''} ${user.email || ''}`
    ),
    [allKnownUsers, assignedUserIdSet]
  );

  const filteredUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    return allKnownUsers.filter(user => {
      const id = user.id || user.user_id;
      const isAssigned = assignedUserIdSet.has(id);
      if (scope === 'assigned' && !isAssigned) return false;
      if (scope === 'available' && isAssigned) return false;
      if (scope === 'active' && user.is_active === false) return false;
      if (scope === 'inactive' && user.is_active !== false) return false;
      if (!query) return true;
      const haystack = [
        composeUserFullName(user),
        user.full_name,
        user.username,
        user.email,
        user.role,
        user.department,
        profile?.name,
        isAssigned ? 'assigned' : 'available'
      ].join(' ').toLowerCase();
      return haystack.includes(query);
    });
  }, [allKnownUsers, assignedUserIdSet, profile?.name, scope, search]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / Number(pageSize)));
  const currentPage = Math.min(page, totalPages);
  const pagedUsers = useMemo(() => {
    const start = (currentPage - 1) * Number(pageSize);
    return filteredUsers.slice(start, start + Number(pageSize));
  }, [currentPage, filteredUsers, pageSize]);
  const pendingRemovalName = composeUserFullName(pendingRemoval) || pendingRemoval?.username || pendingRemoval?.email || 'this user';
  const pendingDeleteName = composeUserFullName(pendingDelete) || pendingDelete?.username || pendingDelete?.email || 'this user';

  useEffect(() => {
    if (!isOpen) {
      setPendingRemoval(null);
      setPendingDelete(null);
      setSelectedUser('');
      setSearch('');
      setScope('assigned');
      setPage(1);
    }
  }, [isOpen]);

  useEffect(() => {
    setPage(1);
  }, [search, scope, pageSize]);

  const getDisplayName = (user) => composeUserFullName(user) || user?.full_name || user?.username || user?.email || 'Unnamed User';
  const getInitials = (user) => {
    const name = getDisplayName(user);
    return name.split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]).join('').toUpperCase() || 'U';
  };
  const getRoleLabel = (role) => ROLE_OPTIONS.find(option => option.value === role)?.label || role || 'Not set';
  const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[min(1180px,calc(100vw-2rem))] max-w-none">
        <DialogHeader>
          <DialogTitle>Manage Users - {profile?.name}</DialogTitle>
          <DialogDescription>Search, assign, edit, and deactivate users connected to this access profile.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-3">
          <div className="grid gap-3 lg:grid-cols-[1.6fr_160px_130px_1.4fr_auto_auto]">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search users by name, alias, email, role..."
                className="pl-9"
              />
            </div>
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="assigned">Assigned</SelectItem>
                <SelectItem value="all">All users</SelectItem>
                <SelectItem value="available">Available</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            <Select value={pageSize} onValueChange={setPageSize}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10 / page</SelectItem>
                <SelectItem value="25">25 / page</SelectItem>
                <SelectItem value="50">50 / page</SelectItem>
              </SelectContent>
            </Select>
            <Select value={selectedUser} onValueChange={setSelectedUser}>
              <SelectTrigger>
                <SelectValue placeholder="Add existing user..." />
              </SelectTrigger>
              <SelectContent>
                {availableUsers.length === 0 ? (
                  <SelectItem value="none" disabled>No available users</SelectItem>
                ) : availableUsers.map(user => (
                  <SelectItem key={user.id} value={String(user.id)}>
                    {getDisplayName(user)} {user.username ? `(@${user.username})` : ''} - {user.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={() => { onAssign(Number(selectedUser)); setSelectedUser(''); }}
                    disabled={!selectedUser || selectedUser === 'none'}
                  >
                    <UserPlus className="h-4 w-4" />
                    <span className="sr-only">Add selected user to profile</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Add selected user to profile</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Button variant="outline" onClick={onNewUser}>
              <Plus className="mr-2 h-4 w-4" />
              New User
            </Button>
          </div>

          <div className="overflow-hidden rounded-lg border">
            <div className="max-h-[55vh] overflow-auto">
              <table className="w-full min-w-[1040px] text-sm">
                <thead className="sticky top-0 z-10 bg-muted/70 text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-3 py-3 text-left">User</th>
                    <th className="px-3 py-3 text-left">Email</th>
                    <th className="px-3 py-3 text-left">Role</th>
                    <th className="px-3 py-3 text-left">Profile</th>
                    <th className="px-3 py-3 text-left">Status</th>
                    <th className="px-3 py-3 text-left">Created</th>
                    <th className="px-3 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedUsers.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-3 py-10 text-center text-muted-foreground">
                        No users match the current filters.
                      </td>
                    </tr>
                  ) : pagedUsers.map(user => {
                    const id = user.id || user.user_id;
                    const isAssigned = assignedUserIdSet.has(id);
                    const assignment = assignmentByUserId.get(id);
                    return (
                      <tr key={id} className="border-t transition-colors hover:bg-muted/30">
                        <td className="px-3 py-3">
                          <div className="flex min-w-0 items-center gap-3">
                            <Avatar className="h-9 w-9 border">
                              {user.photo_url ? <AvatarImage src={user.photo_url} alt={getDisplayName(user)} /> : null}
                              <AvatarFallback>{getInitials(user)}</AvatarFallback>
                            </Avatar>
                            <div className="min-w-0">
                              <p className="truncate font-medium text-foreground">{getDisplayName(user)}</p>
                              <p className="truncate text-xs text-muted-foreground">
                                {user.username ? `@${user.username}` : 'No alias/user'}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-muted-foreground">{user.email || '-'}</td>
                        <td className="px-3 py-3">
                          <Badge variant="secondary">{getRoleLabel(user.role)}</Badge>
                        </td>
                        <td className="px-3 py-3">
                          {isAssigned ? (
                            <div className="flex flex-wrap items-center gap-1">
                              <Badge className="bg-emerald-600 hover:bg-emerald-600">{profile?.name}</Badge>
                              {assignment?.is_primary ? <Badge variant="outline">Primary</Badge> : null}
                            </div>
                          ) : (
                            <Badge variant="outline">Not assigned</Badge>
                          )}
                        </td>
                        <td className="px-3 py-3">
                          <Badge
                            variant="outline"
                            className={cn(
                              user.is_active === false
                                ? 'border-slate-300 bg-slate-50 text-slate-600'
                                : 'border-emerald-300 bg-emerald-50 text-emerald-700'
                            )}
                          >
                            {user.is_active === false ? 'Inactive' : 'Active'}
                          </Badge>
                        </td>
                        <td className="px-3 py-3 text-muted-foreground">{formatDate(user.created_at)}</td>
                        <td className="px-3 py-3">
                          <div className="flex justify-end gap-1">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button variant="ghost" size="icon" onClick={() => onEditUser(user)}>
                                    <Edit className="h-4 w-4" />
                                    <span className="sr-only">Edit user</span>
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Edit user</TooltipContent>
                              </Tooltip>
                              {isAssigned ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button variant="ghost" size="icon" onClick={() => setPendingRemoval(user)}>
                                      <UserMinus className="h-4 w-4 text-destructive" />
                                      <span className="sr-only">Remove from profile</span>
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Remove from profile</TooltipContent>
                                </Tooltip>
                              ) : (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button variant="ghost" size="icon" onClick={() => onAssign(id)}>
                                      <UserPlus className="h-4 w-4 text-emerald-600" />
                                      <span className="sr-only">Assign to profile</span>
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Assign to profile</TooltipContent>
                                </Tooltip>
                              )}
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button variant="ghost" size="icon" onClick={() => setPendingDelete(user)}>
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                    <span className="sr-only">Deactivate user</span>
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Deactivate user</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 border-t bg-background px-3 py-3 text-sm">
              <span className="text-muted-foreground">
                Showing {filteredUsers.length === 0 ? 0 : ((currentPage - 1) * Number(pageSize)) + 1}
                -{Math.min(currentPage * Number(pageSize), filteredUsers.length)} of {filteredUsers.length} users
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setPage(prev => Math.max(1, prev - 1))} disabled={currentPage <= 1}>
                  Previous
                </Button>
                <Button variant="outline" size="sm" onClick={() => setPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage >= totalPages}>
                  Next
                </Button>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
      <AlertDialog open={!!pendingRemoval} onOpenChange={(open) => !open && setPendingRemoval(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove User From Profile?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove {pendingRemovalName} from the {profile?.name} access profile.
              The user account will remain active, but this profile's permissions will no longer apply.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async () => {
                if (!pendingRemoval) return;
                const userId = pendingRemoval.user_id || pendingRemoval.id;
                setPendingRemoval(null);
                await onUnassign(userId);
              }}
            >
              Remove User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!pendingDelete} onOpenChange={(open) => !open && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate User?</AlertDialogTitle>
            <AlertDialogDescription>
              This will deactivate {pendingDeleteName}. The user will no longer be able to access EzERP,
              but the account record remains preserved for audit/history.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async () => {
                if (!pendingDelete) return;
                const userId = pendingDelete.user_id || pendingDelete.id;
                setPendingDelete(null);
                await onDeleteUser(userId);
              }}
            >
              Deactivate User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};

const generateTemporaryPassword = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%';
  const values = new Uint32Array(14);
  window.crypto.getRandomValues(values);
  return Array.from(values, value => chars[value % chars.length]).join('');
};

const composeUserFullName = (user = {}) => {
  const { first_name, middle_name, last_name, full_name } = user || {};
  const name = [first_name, middle_name, last_name]
    .map(part => String(part || '').trim())
    .filter(Boolean)
    .join(' ');
  return name || String(full_name || '').trim();
};

const CreateUserDialog = ({ isOpen, onClose, onCreate, clients, profiles, token, grantableMeta }) => {
  const [form, setForm] = useState({
    full_name: '',
    first_name: '',
    middle_name: '',
    last_name: '',
    username: '',
    email: '',
    phone: '',
    department: '',
    password: '',
    role: 'operator',
    client_id: '',
    business_id: '',
    is_active: true,
    is_client_admin: false,
    is_business_admin: false,
    all_businesses_access: false,
    profile_ids: []
  });
  const [businesses, setBusinesses] = useState([]);
  const [loadingBusinesses, setLoadingBusinesses] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const photoInputRef = useRef(null);

  const activeProfiles = useMemo(
    () => sortByText(
      profiles.filter(profile => profile.is_active !== false),
      profile => profile.name
    ),
    [profiles]
  );
  const sortedClients = useMemo(
    () => sortByText(clients, client => client.name),
    [clients]
  );
  const sortedBusinesses = useMemo(
    () => sortByText(businesses, business => business.name),
    [businesses]
  );
  const canGrantAdminRoles = Boolean(grantableMeta?.can_grant_admin_roles);
  const canGrantClientAdmin = Boolean(grantableMeta?.is_platform_admin || grantableMeta?.is_client_admin);
  const displayName = useMemo(() => composeUserFullName(form), [form]);
  const userInitials = useMemo(() => {
    const parts = displayName.trim().split(/\s+/).filter(Boolean);
    return parts.length ? parts.slice(0, 2).map(part => part[0]).join('').toUpperCase() : 'U';
  }, [displayName]);

  useEffect(() => {
    if (!isOpen) return;
    setForm({
      full_name: '',
      first_name: '',
      middle_name: '',
      last_name: '',
      username: '',
      email: '',
      phone: '',
      department: '',
      password: generateTemporaryPassword(),
      role: 'operator',
      client_id: '',
      business_id: '',
      is_active: true,
      is_client_admin: false,
      is_business_admin: false,
      all_businesses_access: false,
      profile_ids: activeProfiles[0]?.id ? [activeProfiles[0].id] : []
    });
    setBusinesses([]);
    setShowPassword(false);
    setPhotoFile(null);
    setPhotoPreview('');
  }, [isOpen, activeProfiles]);

  useEffect(() => {
    return () => {
      if (photoPreview) {
        URL.revokeObjectURL(photoPreview);
      }
    };
  }, [photoPreview]);

  const loadBusinessesForClient = async (clientId) => {
    if (!clientId) {
      setBusinesses([]);
      return;
    }
    setLoadingBusinesses(true);
    try {
      const res = await fetch(`${API_URL}/api/tenants/clients/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to load company units');
      }
      const nextBusinesses = sortByText(data.businesses || [], business => business.name);
      setBusinesses(nextBusinesses);
      setForm(prev => ({
        ...prev,
        business_id: nextBusinesses[0]?.id ? String(nextBusinesses[0].id) : '',
        all_businesses_access: false
      }));
    } catch (err) {
      setBusinesses([]);
      setForm(prev => ({ ...prev, business_id: '', all_businesses_access: false }));
      toast.error(err.message || 'Failed to load company units');
    } finally {
      setLoadingBusinesses(false);
    }
  };

  const handleClientChange = (clientId) => {
    setForm(prev => ({
      ...prev,
      client_id: clientId,
      business_id: '',
      all_businesses_access: false
    }));
    loadBusinessesForClient(clientId);
  };

  const toggleProfile = (profileId, checked) => {
    setForm(prev => ({
      ...prev,
      profile_ids: checked
        ? Array.from(new Set([...prev.profile_ids, profileId]))
        : prev.profile_ids.filter(id => id !== profileId)
    }));
  };

  const handlePhotoChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Select a valid image file');
      event.target.value = '';
      return;
    }
    if (photoPreview) {
      URL.revokeObjectURL(photoPreview);
    }
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async () => {
    const fullName = composeUserFullName(form);
    if (!form.first_name.trim()) {
      toast.error('First name is required');
      return;
    }
    if (!form.last_name.trim()) {
      toast.error('Last name is required');
      return;
    }
    if (!form.email.trim()) {
      toast.error('Email is required');
      return;
    }
    if (!form.password.trim()) {
      toast.error('Temporary password is required');
      return;
    }
    if (!form.client_id) {
      toast.error('Company is required');
      return;
    }
    if (businesses.length > 0 && !form.business_id && !form.all_businesses_access) {
      toast.error('Unit is required');
      return;
    }
    if (form.profile_ids.length === 0) {
      toast.error('Select at least one access profile');
      return;
    }

    const selectedBusinessIds = form.all_businesses_access
      ? businesses.map(business => business.id)
      : (form.business_id ? [Number(form.business_id)] : []);

    setSubmitting(true);
    try {
      await onCreate({
        ...form,
        full_name: fullName,
        first_name: form.first_name.trim(),
        middle_name: form.middle_name.trim() || null,
        last_name: form.last_name.trim(),
        username: form.username.trim() || null,
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        department: form.department.trim() || null,
        password: form.password,
        client_id: Number(form.client_id),
        business_id: form.business_id ? Number(form.business_id) : null,
        allowed_business_ids: selectedBusinessIds,
        profile_ids: form.profile_ids
      }, photoFile);
      onClose();
    } catch (err) {
      toast.error(err.message || 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Create User</DialogTitle>
          <DialogDescription>Add a user and assign company access.</DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[68vh] pr-4">
          <div className="space-y-5 py-2">
            <div className="rounded-lg border p-4">
              <div className="mb-4 flex items-center gap-4">
                <Avatar className="h-16 w-16 border">
                  {photoPreview ? <AvatarImage src={photoPreview} alt={displayName || 'New user'} /> : null}
                  <AvatarFallback>{userInitials}</AvatarFallback>
                </Avatar>
                <div className="space-y-2">
                  <Label>Avatar</Label>
                  <div>
                    <input
                      ref={photoInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handlePhotoChange}
                    />
                    <Button type="button" variant="outline" size="sm" onClick={() => photoInputRef.current?.click()}>
                      <Camera className="mr-2 h-4 w-4" />
                      Choose Photo
                    </Button>
                  </div>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="new-user-first-name">First Name</Label>
                  <Input
                    id="new-user-first-name"
                    value={form.first_name}
                    onChange={(e) => setForm(prev => ({ ...prev, first_name: e.target.value }))}
                    placeholder="First name"
                    data-testid="new-user-first-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-user-middle-name">Middle Name</Label>
                  <Input
                    id="new-user-middle-name"
                    value={form.middle_name}
                    onChange={(e) => setForm(prev => ({ ...prev, middle_name: e.target.value }))}
                    placeholder="Middle name"
                    data-testid="new-user-middle-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-user-last-name">Last Name</Label>
                  <Input
                    id="new-user-last-name"
                    value={form.last_name}
                    onChange={(e) => setForm(prev => ({ ...prev, last_name: e.target.value }))}
                    placeholder="Last name"
                    data-testid="new-user-last-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-user-alias">Alias / User</Label>
                  <Input
                    id="new-user-alias"
                    value={form.username}
                    onChange={(e) => setForm(prev => ({ ...prev, username: e.target.value }))}
                    placeholder="alias.user"
                    data-testid="new-user-alias"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-user-email">Email</Label>
                  <Input
                    id="new-user-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="user@company.com"
                    data-testid="new-user-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-user-phone">Phone</Label>
                  <Input
                    id="new-user-phone"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="Phone number"
                    data-testid="new-user-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-user-department">Department</Label>
                  <Input
                    id="new-user-department"
                    value={form.department}
                    onChange={(e) => setForm(prev => ({ ...prev, department: e.target.value }))}
                    placeholder="Department"
                    data-testid="new-user-department"
                  />
                </div>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Company</Label>
                <Select value={form.client_id} onValueChange={handleClientChange}>
                  <SelectTrigger data-testid="new-user-company-select">
                    <SelectValue placeholder="Select company" />
                  </SelectTrigger>
                  <SelectContent>
                    {sortedClients.map(client => (
                      <SelectItem key={client.id} value={String(client.id)}>
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Unit</Label>
                <Select
                  value={form.business_id}
                  onValueChange={(value) => setForm(prev => ({ ...prev, business_id: value }))}
                  disabled={!form.client_id || loadingBusinesses || form.all_businesses_access || businesses.length === 0}
                >
                  <SelectTrigger data-testid="new-user-unit-select">
                    <SelectValue placeholder={loadingBusinesses ? 'Loading units...' : businesses.length ? 'Select unit' : 'No units'} />
                  </SelectTrigger>
                  <SelectContent>
                    {sortedBusinesses.map(business => (
                      <SelectItem key={business.id} value={String(business.id)}>
                        {business.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(role) => setForm(prev => ({ ...prev, role }))}>
                  <SelectTrigger data-testid="new-user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTIONS.map(role => (
                      <SelectItem key={role.value} value={role.value} disabled={role.value === 'admin' && !canGrantAdminRoles}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end gap-3 rounded-lg border p-3">
                <Switch
                  id="new-user-active"
                  checked={form.is_active}
                  onCheckedChange={(checked) => setForm(prev => ({ ...prev, is_active: checked }))}
                />
                <Label htmlFor="new-user-active" className="cursor-pointer">Active user</Label>
              </div>
              <div className="flex items-end gap-3 rounded-lg border p-3">
                <Switch
                  id="new-user-all-units"
                  checked={form.all_businesses_access}
                  onCheckedChange={(checked) => setForm(prev => ({ ...prev, all_businesses_access: checked }))}
                  disabled={businesses.length === 0}
                />
                <Label htmlFor="new-user-all-units" className="cursor-pointer">All units</Label>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="flex items-center gap-3 rounded-lg border p-3">
                <Switch
                  id="new-user-business-admin"
                  checked={form.is_business_admin}
                  onCheckedChange={(checked) => setForm(prev => ({ ...prev, is_business_admin: checked }))}
                  disabled={!canGrantAdminRoles}
                />
                <Label htmlFor="new-user-business-admin" className="cursor-pointer">Business admin</Label>
              </div>
              <div className="flex items-center gap-3 rounded-lg border p-3">
                <Switch
                  id="new-user-client-admin"
                  checked={form.is_client_admin}
                  onCheckedChange={(checked) => setForm(prev => ({ ...prev, is_client_admin: checked }))}
                  disabled={!canGrantClientAdmin}
                />
                <Label htmlFor="new-user-client-admin" className="cursor-pointer">Client admin</Label>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Access Profiles</Label>
              <div className="grid max-h-40 gap-2 overflow-y-auto rounded-lg border p-3 md:grid-cols-2">
                {activeProfiles.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No active profiles available.</p>
                ) : (
                  activeProfiles.map(profile => (
                    <label key={profile.id} className="flex cursor-pointer items-center gap-3 rounded-md border p-2 hover:bg-muted/40">
                      <Checkbox
                        checked={form.profile_ids.includes(profile.id)}
                        onCheckedChange={(checked) => toggleProfile(profile.id, checked === true)}
                      />
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium">{profile.name}</span>
                        <span className="block truncate text-xs text-muted-foreground">{profile.template}</span>
                      </span>
                    </label>
                  ))
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="new-user-password">Temporary Password</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    id="new-user-password"
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={(e) => setForm(prev => ({ ...prev, password: e.target.value }))}
                    className="pr-10"
                    data-testid="new-user-password"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2"
                    onClick={() => setShowPassword(prev => !prev)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setForm(prev => ({ ...prev, password: generateTemporaryPassword() }))}
                >
                  Generate
                </Button>
              </div>
            </div>
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="save-new-user-btn">
            {submitting ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <UserPlus className="h-4 w-4 mr-2" />}
            Create User
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const EditUserDialog = ({ user, isOpen, onClose, onSave, grantableMeta }) => {
  const [form, setForm] = useState({
    first_name: '',
    middle_name: '',
    last_name: '',
    username: '',
    email: '',
    phone: '',
    department: '',
    role: 'operator',
    is_active: true
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const photoInputRef = useRef(null);
  const canGrantAdminRoles = Boolean(grantableMeta?.can_grant_admin_roles);
  const displayName = useMemo(() => composeUserFullName(form) || form.email || 'User', [form]);
  const userInitials = useMemo(() => {
    const parts = displayName.trim().split(/\s+/).filter(Boolean);
    return parts.length ? parts.slice(0, 2).map(part => part[0]).join('').toUpperCase() : 'U';
  }, [displayName]);

  useEffect(() => {
    if (!isOpen || !user) return;
    setForm({
      first_name: user.first_name || '',
      middle_name: user.middle_name || '',
      last_name: user.last_name || '',
      username: user.username || '',
      email: user.email || '',
      phone: user.phone || '',
      department: user.department || '',
      role: user.role || 'operator',
      is_active: user.is_active !== false
    });
    setPhotoFile(null);
    setPhotoPreview('');
  }, [isOpen, user]);

  useEffect(() => {
    return () => {
      if (photoPreview) URL.revokeObjectURL(photoPreview);
    };
  }, [photoPreview]);

  const handlePhotoChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Select a valid image file');
      event.target.value = '';
      return;
    }
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async () => {
    const userId = user?.id || user?.user_id;
    if (!userId) return;
    if (!form.first_name.trim()) {
      toast.error('First name is required');
      return;
    }
    if (!form.last_name.trim()) {
      toast.error('Last name is required');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        middle_name: form.middle_name.trim() || null,
        last_name: form.last_name.trim(),
        full_name: composeUserFullName(form),
        username: form.username.trim() || null,
        phone: form.phone.trim() || null,
        department: form.department.trim() || null,
        role: form.role,
        is_active: form.is_active
      };
      await onSave(userId, payload, photoFile);
      onClose();
    } catch (err) {
      toast.error(err.message || 'Failed to update user');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Edit User</DialogTitle>
          <DialogDescription>Update the account details stored for this user.</DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[68vh] pr-4">
          <div className="space-y-5 py-2">
            <div className="rounded-lg border p-4">
              <div className="mb-4 flex items-center gap-4">
                <Avatar className="h-16 w-16 border">
                  {photoPreview || user?.photo_url ? (
                    <AvatarImage src={photoPreview || user?.photo_url} alt={displayName} />
                  ) : null}
                  <AvatarFallback>{userInitials}</AvatarFallback>
                </Avatar>
                <div className="space-y-2">
                  <Label>Avatar</Label>
                  <div>
                    <input
                      ref={photoInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handlePhotoChange}
                    />
                    <Button type="button" variant="outline" size="sm" onClick={() => photoInputRef.current?.click()}>
                      <Camera className="mr-2 h-4 w-4" />
                      Choose Photo
                    </Button>
                  </div>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="edit-user-first-name">First Name</Label>
                  <Input
                    id="edit-user-first-name"
                    value={form.first_name}
                    onChange={(e) => setForm(prev => ({ ...prev, first_name: e.target.value }))}
                    placeholder="First name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-user-middle-name">Middle Name</Label>
                  <Input
                    id="edit-user-middle-name"
                    value={form.middle_name}
                    onChange={(e) => setForm(prev => ({ ...prev, middle_name: e.target.value }))}
                    placeholder="Middle name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-user-last-name">Last Name</Label>
                  <Input
                    id="edit-user-last-name"
                    value={form.last_name}
                    onChange={(e) => setForm(prev => ({ ...prev, last_name: e.target.value }))}
                    placeholder="Last name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-user-alias">Alias / User</Label>
                  <Input
                    id="edit-user-alias"
                    value={form.username}
                    onChange={(e) => setForm(prev => ({ ...prev, username: e.target.value }))}
                    placeholder="alias.user"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-user-email">Email</Label>
                  <Input
                    id="edit-user-email"
                    type="email"
                    value={form.email}
                    disabled
                    className="bg-muted/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-user-phone">Phone</Label>
                  <Input
                    id="edit-user-phone"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="Phone number"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-user-department">Department</Label>
                  <Input
                    id="edit-user-department"
                    value={form.department}
                    onChange={(e) => setForm(prev => ({ ...prev, department: e.target.value }))}
                    placeholder="Department"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select value={form.role} onValueChange={(role) => setForm(prev => ({ ...prev, role }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLE_OPTIONS.map(role => (
                        <SelectItem key={role.value} value={role.value} disabled={role.value === 'admin' && !canGrantAdminRoles}>
                          {role.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end gap-3 rounded-lg border p-3">
                  <Switch
                    id="edit-user-active"
                    checked={form.is_active}
                    onCheckedChange={(checked) => setForm(prev => ({ ...prev, is_active: checked }))}
                  />
                  <Label htmlFor="edit-user-active" className="cursor-pointer">Active user</Label>
                </div>
              </div>
            </div>
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main component
export default function AccessProfiles() {
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [matrix, setMatrix] = useState({ columns: [], rows: [], modules: [] });
  const [profiles, setProfiles] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedModule, setSelectedModule] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedModules, setExpandedModules] = useState({});
  const [showOnlyGrantable, setShowOnlyGrantable] = useState(false);
  const [grantablePermissions, setGrantablePermissions] = useState([]);
  const [grantableMeta, setGrantableMeta] = useState({
    is_platform_admin: false,
    is_client_admin: false,
    is_business_admin: false,
    can_grant_admin_roles: false
  });
  const [activeTab, setActiveTab] = useState('matrix');
  
  // Client and Subsidiary filters
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('all');
  const [subsidiaries, setSubsidiaries] = useState([]);
  const [selectedSubsidiary, setSelectedSubsidiary] = useState('all');
  
  // Dialogs
  const [profileDialog, setProfileDialog] = useState({ open: false, profile: null });
  const [userDialog, setUserDialog] = useState({ open: false, profile: null });
  const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);
  const [editUserDialog, setEditUserDialog] = useState({ open: false, user: null });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, profile: null });
  const [monitoringRows, setMonitoringRows] = useState([]);
  const [monitoringMeta, setMonitoringMeta] = useState({
    page: 1,
    page_size: 25,
    total: 0,
    summary: { total_users: 0, online_now: 0, logged_on_now: 0, logged_this_week: 0, logged_this_month: 0 }
  });
  const [monitoringLoading, setMonitoringLoading] = useState(false);
  const [monitoringError, setMonitoringError] = useState('');
  const [monitoringFilters, setMonitoringFilters] = useState({
    user: '',
    empresa: '',
    status: 'all',
    profile: 'all',
    dateFrom: '',
    dateTo: ''
  });
  const [selectedMonitoringUserIds, setSelectedMonitoringUserIds] = useState([]);
  const [monitoringSort, setMonitoringSort] = useState({ key: 'status', direction: 'asc' });

  useEffect(() => {
    const requestedTab = searchParams.get('tab');
    const allowedTabs = ['matrix', 'profiles', 'user-monitoring'];
    if (allowedTabs.includes(requestedTab)) {
      setActiveTab(requestedTab);
    }
  }, [searchParams]);

  useEffect(() => {
    if (activeTab !== 'user-monitoring' || window.location.hash !== '#last-activity') return;

    window.requestAnimationFrame(() => {
      document.getElementById('last-activity')?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center'
      });
    });
  }, [activeTab]);

  // Fetch data
  const fetchMatrix = async () => {
    try {
      const res = await fetch(`${API_URL}/api/access-profiles/matrix`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMatrix(data);
        // Expand all modules by default
        const expanded = {};
        data.modules.forEach(m => expanded[m] = true);
        setExpandedModules(expanded);
      }
    } catch (err) {
      console.error('Error fetching matrix:', err);
    }
  };

  const fetchProfiles = async () => {
    try {
      const res = await fetch(`${API_URL}/api/access-profiles/profiles?include_inactive=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setProfiles(data.profiles);
      }
    } catch (err) {
      console.error('Error fetching profiles:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/auth/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        // API returns array directly, not wrapped in users object
        setUsers(Array.isArray(data) ? data : data.users || []);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const hasMonitoringNarrowingFilter = (filters = monitoringFilters) => {
    return Boolean(
      filters.user.trim() ||
      filters.empresa.trim() ||
      filters.status !== 'all' ||
      filters.profile !== 'all'
    );
  };

  const fetchUserMonitoring = async (page = 1, filters = monitoringFilters) => {
    const hasDateRange = Boolean(filters.dateFrom || filters.dateTo);
    if (hasDateRange && !hasMonitoringNarrowingFilter(filters)) {
      const message = 'Date range requires User, Empresa, Status, or Profile filter.';
      setMonitoringError(message);
      toast.error(message);
      return;
    }

    setMonitoringLoading(true);
    setMonitoringError('');
    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: '25'
      });
      if (filters.user.trim()) params.set('user', filters.user.trim());
      if (filters.empresa.trim()) params.set('empresa', filters.empresa.trim());
      if (filters.status !== 'all') params.set('status', filters.status);
      if (filters.profile !== 'all') params.set('profile', filters.profile);
      if (filters.dateFrom) params.set('date_from', filters.dateFrom);
      if (filters.dateTo) params.set('date_to', filters.dateTo);

      const res = await fetch(`${API_URL}/api/access-profiles/user-access-monitoring?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to load user access monitoring');
      }
      setMonitoringRows(data.rows || []);
      setMonitoringMeta({
        page: data.page || page,
        page_size: data.page_size || 25,
        total: data.total || 0,
        summary: data.summary || { total_users: 0, online_now: 0, logged_on_now: 0, logged_this_week: 0, logged_this_month: 0 }
      });
    } catch (err) {
      const message = err.message || 'Failed to load user access monitoring';
      setMonitoringError(message);
      toast.error(message);
    } finally {
      setMonitoringLoading(false);
    }
  };

  const fetchGrantablePermissions = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/my-grantable-permissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        // Extract permission codes from grantable permissions
        setGrantablePermissions(data.grantable_permissions || []);
        setGrantableMeta({
          is_platform_admin: Boolean(data.is_platform_admin),
          is_client_admin: Boolean(data.is_client_admin),
          is_business_admin: Boolean(data.is_business_admin),
          can_grant_admin_roles: Boolean(data.can_grant_admin_roles)
        });
      }
    } catch (err) {
      console.error('Error fetching grantable permissions:', err);
      // Fallback: if admin, assume all are grantable
      setGrantablePermissions([]);
      setGrantableMeta({
        is_platform_admin: false,
        is_client_admin: false,
        is_business_admin: false,
        can_grant_admin_roles: false
      });
    }
  };

  // Fetch clients and their subsidiaries (businesses)
  const fetchClients = async () => {
    try {
      const res = await fetch(`${API_URL}/api/tenants/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setClients(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error fetching clients:', err);
      // If API not available, use empty list
      setClients([]);
    }
  };

  // Fetch client details (with businesses/subsidiaries)
  const fetchClientDetails = async (clientId) => {
    if (clientId === 'all' || !clientId) {
      setSubsidiaries([]);
      setSelectedSubsidiary('all');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/tenants/clients/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSubsidiaries(data.businesses || []);
      }
    } catch (err) {
      console.error('Error fetching client details:', err);
      setSubsidiaries([]);
    }
  };

  // Handle client change
  const handleClientChange = (clientId) => {
    setSelectedClient(clientId);
    setSelectedSubsidiary('all');
    if (clientId !== 'all') {
      fetchClientDetails(clientId);
    } else {
      setSubsidiaries([]);
    }
  };

  const seedData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/access-profiles/seed`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.seeded) {
          toast.success(`Seeded ${data.capabilities} capabilities and ${data.profiles} profiles`);
        }
        return data.seeded;
      }
    } catch (err) {
      console.error('Error seeding:', err);
    }
    return false;
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await seedData();
      await Promise.all([fetchMatrix(), fetchProfiles(), fetchUsers(), fetchGrantablePermissions(), fetchClients(), fetchUserMonitoring()]);
      setLoading(false);
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Handlers
  const handlePermissionChange = async (profileId, capabilityId, newState) => {
    try {
      const res = await fetch(
        `${API_URL}/api/access-profiles/matrix/${profileId}/capability/${capabilityId}?state=${newState}`,
        { method: 'PUT', headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        // Update local state
        setMatrix(prev => ({
          ...prev,
          rows: prev.rows.map(row => {
            if (row.id !== profileId) return row;
            const permissions = row.permissions.map(p => 
              p.capability_id === capabilityId ? { ...p, state: newState } : p
            );
            return { ...row, permissions };
          })
        }));
        toast.success('Permission updated');
      }
    } catch (err) {
      toast.error('Failed to update permission');
    }
  };

  const handleSaveProfile = async (form) => {
    try {
      const isEdit = !!profileDialog.profile;
      const url = isEdit 
        ? `${API_URL}/api/access-profiles/profiles/${profileDialog.profile.id}`
        : `${API_URL}/api/access-profiles/profiles`;
      
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify(form)
      });

      if (res.ok) {
        toast.success(isEdit ? 'Profile updated' : 'Profile created');
        setProfileDialog({ open: false, profile: null });
        await Promise.all([fetchMatrix(), fetchProfiles()]);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save profile');
      }
    } catch (err) {
      toast.error('Failed to save profile');
    }
  };

  const handleDeleteProfile = async () => {
    if (!deleteConfirm.profile) return;
    try {
      const res = await fetch(`${API_URL}/api/access-profiles/profiles/${deleteConfirm.profile.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Profile deleted');
        setDeleteConfirm({ open: false, profile: null });
        await Promise.all([fetchMatrix(), fetchProfiles()]);
      }
    } catch (err) {
      toast.error('Failed to delete profile');
    }
  };

  const refreshOpenUserProfile = async () => {
    if (!userDialog.profile?.id) return;
    const detailRes = await fetch(`${API_URL}/api/access-profiles/profiles/${userDialog.profile.id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (detailRes.ok) {
      const data = await detailRes.json();
      setUserDialog(prev => ({ ...prev, profile: data }));
    }
  };

  const handleAssignUser = async (userId) => {
    if (!userDialog.profile) return;
    try {
      const res = await fetch(`${API_URL}/api/access-profiles/profiles/${userDialog.profile.id}/users`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ user_id: userId })
      });
      if (res.ok) {
        toast.success('User assigned');
        await Promise.all([refreshOpenUserProfile(), fetchProfiles()]);
      }
    } catch (err) {
      toast.error('Failed to assign user');
    }
  };

  const handleUnassignUser = async (userId) => {
    if (!userDialog.profile) return;
    try {
      const res = await fetch(
        `${API_URL}/api/access-profiles/profiles/${userDialog.profile.id}/users/${userId}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        toast.success('User removed');
        await Promise.all([refreshOpenUserProfile(), fetchProfiles()]);
      }
    } catch (err) {
      toast.error('Failed to remove user');
    }
  };

  const handleOpenUserDialog = async (profile) => {
    try {
      const res = await fetch(`${API_URL}/api/access-profiles/profiles/${profile.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUserDialog({ open: true, profile: data });
      }
    } catch (err) {
      toast.error('Failed to load profile details');
    }
  };

  const handleCreateUser = async (payload, photoFile) => {
    const res = await fetch(`${API_URL}/api/admin/users`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.detail || 'Failed to create user');
    }

    if (photoFile && data.id) {
      const photoForm = new FormData();
      photoForm.append('file', photoFile);
      const photoRes = await fetch(`${API_URL}/api/admin/users/${data.id}/upload-photo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: photoForm
      });
      if (!photoRes.ok) {
        const photoError = await photoRes.json().catch(() => ({}));
        toast.error(photoError.detail || 'User created, but photo upload failed');
      }
    }

    toast.success(`User created: ${data.full_name || payload.full_name}`);
    await Promise.all([
      fetchUsers(),
      fetchProfiles(),
      fetchMatrix(),
      fetchUserMonitoring(monitoringMeta.page)
    ]);
    await refreshOpenUserProfile();
  };

  const handleUpdateUser = async (userId, payload, photoFile) => {
    const res = await fetch(`${API_URL}/api/admin/users/${userId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.detail || 'Failed to update user');
    }

    if (photoFile) {
      const photoForm = new FormData();
      photoForm.append('file', photoFile);
      const photoRes = await fetch(`${API_URL}/api/admin/users/${userId}/upload-photo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: photoForm
      });
      if (!photoRes.ok) {
        const photoError = await photoRes.json().catch(() => ({}));
        toast.error(photoError.detail || 'User updated, but photo upload failed');
      }
    }

    toast.success('User updated');
    await Promise.all([
      fetchUsers(),
      fetchProfiles(),
      fetchMatrix(),
      fetchUserMonitoring(monitoringMeta.page)
    ]);
    await refreshOpenUserProfile();
  };

  const handleDeactivateUser = async (userId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to deactivate user');
      }
      toast.success('User deactivated');
      await Promise.all([
        fetchUsers(),
        fetchProfiles(),
        fetchMatrix(),
        fetchUserMonitoring(monitoringMeta.page)
      ]);
      await refreshOpenUserProfile();
    } catch (err) {
      toast.error(err.message || 'Failed to deactivate user');
    }
  };

  const handleApplyTemplate = async (profileId, template) => {
    try {
      const res = await fetch(
        `${API_URL}/api/access-profiles/matrix/${profileId}/apply-template?template=${template}`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        toast.success(`Applied ${template} template`);
        await fetchMatrix();
      }
    } catch (err) {
      toast.error('Failed to apply template');
    }
  };

  const normalizedSearchQuery = useMemo(() => searchQuery.trim().toLowerCase(), [searchQuery]);

  const matchingSearchCapabilityIds = useMemo(() => {
    if (!normalizedSearchQuery) return new Set();
    return new Set(
      matrix.columns
        .filter(cap => {
          const moduleConfig = MODULE_CONFIG[cap.module];
          return [
            cap.name,
            cap.code,
            cap.module,
            moduleConfig?.name
          ].filter(Boolean).some(value => value.toLowerCase().includes(normalizedSearchQuery));
        })
        .map(cap => cap.id)
    );
  }, [matrix.columns, normalizedSearchQuery]);

  const profilesById = useMemo(() => {
    return new Map(profiles.map(profile => [profile.id, profile]));
  }, [profiles]);

  const sortedProfiles = useMemo(
    () => sortByText(profiles, profile => profile.name),
    [profiles]
  );

  const sortedClients = useMemo(
    () => sortByText(clients, client => client.name),
    [clients]
  );

  const sortedSubsidiaries = useMemo(
    () => sortByText(subsidiaries, subsidiary => subsidiary.name),
    [subsidiaries]
  );

  const sortedModules = useMemo(
    () => sortByText(matrix.modules, module => MODULE_CONFIG[module]?.name || module),
    [matrix.modules]
  );

  // Filter columns by module and page-level permission search
  const filteredColumns = useMemo(() => {
    let cols = selectedModule === 'all' ? matrix.columns : matrix.columns.filter(c => c.module === selectedModule);
    
    // If showing only grantable, filter to only grantable permissions
    if (showOnlyGrantable && grantablePermissions.length > 0 && !grantablePermissions.includes('*')) {
      cols = cols.filter(c => grantablePermissions.includes(c.code));
    }

    if (normalizedSearchQuery && matchingSearchCapabilityIds.size > 0) {
      cols = cols.filter(c => matchingSearchCapabilityIds.has(c.id));
    }
    
    return cols;
  }, [matrix.columns, selectedModule, showOnlyGrantable, grantablePermissions, normalizedSearchQuery, matchingSearchCapabilityIds]);

  // Check if a capability is grantable by current user
  const isCapabilityGrantable = (capabilityCode) => {
    // If no grantable permissions loaded, assume all are grantable (platform admin)
    if (grantablePermissions.length === 0) return true;
    if (grantablePermissions.includes('*')) return true;
    return grantablePermissions.includes(capabilityCode);
  };

  const profileMatchesSearch = useCallback((profile) => {
    if (!normalizedSearchQuery) return true;
    const assignedUsers = profile?.users || [];
    const searchableValues = [
      profile?.name,
      profile?.template,
      profile?.description,
      profile?.type,
      profile?.user_count != null ? `${profile.user_count} users` : null,
      ...assignedUsers.flatMap(user => [user.full_name, user.username, user.email])
    ];
    return searchableValues
      .filter(Boolean)
      .some(value => value.toString().toLowerCase().includes(normalizedSearchQuery));
  }, [normalizedSearchQuery]);

  // Filter rows by page-level search
  const filteredRows = useMemo(() => {
    if (!normalizedSearchQuery) return matrix.rows;
    return matrix.rows.filter(row => {
      const profile = profilesById.get(row.id);
      const rowMatches = profileMatchesSearch({ ...profile, ...row });
      const permissionMatches = matchingSearchCapabilityIds.size > 0
        && row.permissions?.some(permission =>
          matchingSearchCapabilityIds.has(permission.capability_id)
          && permission.state !== 'not_set'
        );
      return rowMatches || permissionMatches;
    });
  }, [matrix.rows, normalizedSearchQuery, profilesById, matchingSearchCapabilityIds, profileMatchesSearch]);

  const filteredProfiles = useMemo(() => {
    const nextProfiles = !normalizedSearchQuery
      ? sortedProfiles
      : sortedProfiles.filter(profile => profileMatchesSearch(profile));
    return nextProfiles;
  }, [sortedProfiles, normalizedSearchQuery, profileMatchesSearch]);

  // Group columns by module
  const groupedColumns = useMemo(() => {
    const groups = {};
    filteredColumns.forEach(col => {
      if (!groups[col.module]) groups[col.module] = [];
      groups[col.module].push(col);
    });
    return groups;
  }, [filteredColumns]);

  const formatMonitoringDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString();
  };

  const getMonitoringRowStatusClass = (row) => {
    if (row.status_key === 'online') {
      return 'border-l-4 border-l-emerald-500';
    }
    if (row.status_key === 'recently_active') {
      return 'border-l-4 border-l-amber-400';
    }
    return 'border-l-4 border-l-slate-300';
  };

  const updateMonitoringFilter = (key, value) => {
    setMonitoringFilters(prev => ({ ...prev, [key]: value }));
  };

  const resetMonitoringFilters = () => {
    const nextFilters = { user: '', empresa: '', status: 'all', profile: 'all', dateFrom: '', dateTo: '' };
    setMonitoringFilters(nextFilters);
    fetchUserMonitoring(1, nextFilters);
  };

  const totalMonitoringPages = Math.max(1, Math.ceil((monitoringMeta.total || 0) / (monitoringMeta.page_size || 25)));
  const dateFilterNeedsScope = Boolean(
    (monitoringFilters.dateFrom || monitoringFilters.dateTo) &&
    !hasMonitoringNarrowingFilter(monitoringFilters)
  );
  const monitoringVisibleUserIds = useMemo(
    () => monitoringRows.map(row => row.user_id),
    [monitoringRows]
  );
  const selectedMonitoringCount = selectedMonitoringUserIds.length;
  const allMonitoringRowsSelected = monitoringVisibleUserIds.length > 0 &&
    monitoringVisibleUserIds.every(userId => selectedMonitoringUserIds.includes(userId));
  const someMonitoringRowsSelected = monitoringVisibleUserIds.some(userId => selectedMonitoringUserIds.includes(userId));

  const toggleMonitoringRowSelection = (userId) => {
    setSelectedMonitoringUserIds(prev => (
      prev.includes(userId)
        ? prev.filter(selectedId => selectedId !== userId)
        : [...prev, userId]
    ));
  };

  const toggleAllMonitoringRows = (checked) => {
    if (checked) {
      setSelectedMonitoringUserIds(prev => Array.from(new Set([...prev, ...monitoringVisibleUserIds])));
      return;
    }
    setSelectedMonitoringUserIds(prev => prev.filter(userId => !monitoringVisibleUserIds.includes(userId)));
  };

  const getMonitoringSortValue = (row, key) => {
    switch (key) {
      case 'status':
        return row.status || '';
      case 'user':
        return `${row.user_name || ''} ${row.username || ''} ${row.email || ''}`;
      case 'empresa':
        return `${row.empresa || ''} ${row.business || ''}`;
      case 'profile':
        return (row.profiles || []).join(' ');
      case 'login_time':
      case 'logout_time':
      case 'last_activity_at':
        return row[key] ? new Date(row[key]).getTime() : 0;
      case 'duration':
        return row.session_duration || '';
      case 'ip_device':
        return `${row.ip_address || ''} ${row.device || ''}`;
      default:
        return row[key] || '';
    }
  };

  const sortedMonitoringRows = useMemo(() => {
    const direction = monitoringSort.direction === 'asc' ? 1 : -1;
    return [...monitoringRows].sort((a, b) => {
      const aValue = getMonitoringSortValue(a, monitoringSort.key);
      const bValue = getMonitoringSortValue(b, monitoringSort.key);
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return (aValue - bValue) * direction;
      }
      return String(aValue).localeCompare(String(bValue), undefined, { numeric: true, sensitivity: 'base' }) * direction;
    });
  }, [monitoringRows, monitoringSort]);

  const updateMonitoringSort = (key) => {
    setMonitoringSort(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const SortableMonitoringHeader = ({ label, sortKey, className }) => (
    <button
      type="button"
      onClick={() => updateMonitoringSort(sortKey)}
      className={cn(
        'inline-flex items-center gap-1 text-xs font-semibold uppercase text-muted-foreground transition-colors hover:text-foreground',
        className
      )}
      aria-sort={monitoringSort.key === sortKey ? (monitoringSort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <span>{label}</span>
      {monitoringSort.key === sortKey ? (
        <ChevronDown className={cn('h-3 w-3', monitoringSort.direction === 'asc' && 'rotate-180')} />
      ) : (
        <ChevronsUpDown className="h-3 w-3 opacity-60" />
      )}
    </button>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="access-profiles-page">
      {/* Header */}
      <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <div className="min-w-[280px]">
          <h1 className="text-2xl font-bold">Access Profiles & Permissions</h1>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center xl:flex-1 xl:justify-end">
          <div className="relative w-full sm:max-w-[420px]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search profiles, permissions, users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-10 pl-9"
              data-testid="access-profiles-page-search"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => Promise.all([fetchMatrix(), fetchProfiles(), fetchUsers(), fetchUserMonitoring(monitoringMeta.page)])}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={() => setProfileDialog({ open: true, profile: null })} data-testid="create-profile-btn">
              <Plus className="h-4 w-4 mr-2" />
              New Profile
            </Button>
            <Button
              onClick={() => setCreateUserDialogOpen(true)}
              className="bg-emerald-600 text-white hover:bg-emerald-700"
              data-testid="create-user-btn"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              New User
            </Button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <CompactStatCard
          title="Total Profiles"
          value={profiles.length}
          icon={Shield}
          borderClass="border-l-violet-500"
          iconBgClass="bg-violet-500/10"
          iconClass="text-violet-500"
        />
        <CompactStatCard
          title="Capabilities"
          value={matrix.columns.length}
          icon={Settings}
          borderClass="border-l-blue-500"
          iconBgClass="bg-blue-500/10"
          iconClass="text-blue-500"
        />
        <CompactStatCard
          title="Modules"
          value={matrix.modules.length}
          icon={Box}
          borderClass="border-l-emerald-500"
          iconBgClass="bg-emerald-500/10"
          iconClass="text-emerald-500"
        />
        <CompactStatCard
          title="Total Users"
          value={monitoringMeta.summary?.total_users ?? users.length}
          helper={`${monitoringMeta.summary?.logged_this_week || 0} this week`}
          icon={Users}
          borderClass="border-l-amber-500"
          iconBgClass="bg-amber-500/10"
          iconClass="text-amber-600"
        />
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="matrix" data-testid="matrix-tab">
              <LayoutDashboard className="h-4 w-4 mr-2" />
              Permission Matrix
            </TabsTrigger>
            <TabsTrigger value="profiles" data-testid="profiles-tab">
              <Shield className="h-4 w-4 mr-2" />
              Profiles
            </TabsTrigger>
            <TabsTrigger value="user-monitoring" data-testid="user-monitoring-tab">
              <Activity className="h-4 w-4 mr-2" />
              User Monitoring
            </TabsTrigger>
          </TabsList>
          
          {activeTab === 'matrix' && (
          <div className="flex items-center gap-2 flex-wrap">
            {/* Client Filter */}
            <Select value={selectedClient} onValueChange={handleClientChange}>
              <SelectTrigger className="w-[160px]" data-testid="client-filter">
                <Building2 className="h-4 w-4 mr-2" />
                <SelectValue placeholder="All Clients" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Clients</SelectItem>
                {sortedClients.map(c => (
                  <SelectItem key={c.id} value={c.id.toString()}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Subsidiaries Filter */}
            <Select 
              value={selectedSubsidiary} 
              onValueChange={setSelectedSubsidiary}
              disabled={selectedClient === 'all'}
            >
              <SelectTrigger className="w-[160px]" data-testid="subsidiary-filter">
                <Building className="h-4 w-4 mr-2" />
                <SelectValue placeholder="All Subsidiaries" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Subsidiaries</SelectItem>
                {sortedSubsidiaries.map(s => (
                  <SelectItem key={s.id} value={s.id.toString()}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Module Filter */}
            <Select value={selectedModule} onValueChange={setSelectedModule}>
              <SelectTrigger className="w-[180px]" data-testid="module-filter">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Modules</SelectItem>
                {sortedModules.map(m => {
                  const config = MODULE_CONFIG[m] || { name: m, color: '#6366f1' };
                  return (
                    <SelectItem key={m} value={m}>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: config.color }} />
                        {config.name}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2 ml-2 pl-2 border-l">
              <Switch
                id="grantable-toggle"
                checked={showOnlyGrantable}
                onCheckedChange={setShowOnlyGrantable}
                data-testid="grantable-permissions-toggle"
              />
              <Label htmlFor="grantable-toggle" className="text-sm cursor-pointer">
                {showOnlyGrantable ? (
                  <span className="flex items-center gap-1">
                    <Eye className="h-3.5 w-3.5" />
                    Grantable Only
                  </span>
                ) : (
                  <span className="flex items-center gap-1">
                    <EyeOff className="h-3.5 w-3.5" />
                    All Permissions
                  </span>
                )}
              </Label>
            </div>
          </div>
          )}
        </div>

        {/* Permission Matrix Tab */}
        <TabsContent value="matrix" className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Permission Matrix</CardTitle>
              <CardDescription>
                Click on cells to toggle permissions: 
                <span className="inline-flex items-center gap-1 ml-2">
                  <span className="inline-block w-4 h-4 rounded bg-emerald-500"></span> Allow
                </span>
                <span className="inline-flex items-center gap-1 ml-2">
                  <span className="inline-block w-4 h-4 rounded bg-red-500"></span> Deny
                </span>
                <span className="inline-flex items-center gap-1 ml-2">
                  <span className="inline-block w-4 h-4 rounded bg-slate-300 dark:bg-slate-600"></span> Not Set (Inherit)
                </span>
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-auto max-h-[60vh]">
                {/* Header row with module groups */}
                <div className="flex sticky top-0 z-20 bg-card border-b">
                  {/* Empty corner cell */}
                  <div className="sticky left-0 z-30 bg-card min-w-[220px] w-[220px] border-r border-b p-3">
                    <p className="text-sm font-medium text-muted-foreground">Profiles</p>
                  </div>
                  
                  {/* Module headers */}
                  <div className="flex">
                    {Object.entries(groupedColumns).map(([module, caps]) => {
                      const config = MODULE_CONFIG[module] || { name: module, color: '#6366f1' };
                      return (
                        <div 
                          key={module}
                          className="border-l-2 px-2 py-1"
                          style={{ 
                            borderColor: config.color,
                            minWidth: caps.length * 44 + 'px'
                          }}
                        >
                          <p className="text-xs font-semibold" style={{ color: config.color }}>
                            {config.name}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                {/* Capability column headers */}
                <div className="flex sticky top-[41px] z-20 bg-muted/50 border-b">
                  <div className="sticky left-0 z-30 bg-muted/50 min-w-[220px] w-[220px] border-r" />
                  <div className="flex">
                    {filteredColumns.map(cap => {
                      const moduleColor = MODULE_CONFIG[cap.module]?.color || '#6366f1';
                      return (
                        <CapabilityHeader 
                          key={cap.id} 
                          capability={cap} 
                          moduleColor={moduleColor}
                        />
                      );
                    })}
                  </div>
                </div>
                
                {/* Profile rows */}
                {filteredRows.map(row => (
                  <ProfileRow
                    key={row.id}
                    profile={row}
                    columns={filteredColumns}
                    onPermissionChange={handlePermissionChange}
                    onEdit={(p) => setProfileDialog({ open: true, profile: p })}
                    onDelete={(p) => setDeleteConfirm({ open: true, profile: p })}
                    expandedModules={expandedModules}
                    isCapabilityGrantable={isCapabilityGrantable}
                  />
                ))}
                
                {filteredRows.length === 0 && (
                  <div className="p-8 text-center text-muted-foreground">
                    <Shield className="h-12 w-12 mx-auto mb-4 opacity-20" />
                    <p>No profiles found</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Profiles List Tab */}
        <TabsContent value="profiles" className="space-y-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredProfiles.map(profile => {
              const Icon = ICON_MAP[profile.icon] || Shield;
              const templateConfig = DEFAULT_TEMPLATES.find(t => t.value === profile.template) || DEFAULT_TEMPLATES[5];
              
              return (
                <Card key={profile.id} className="overflow-hidden">
                  <div className="h-2" style={{ backgroundColor: profile.color }} />
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: profile.color }}
                        >
                          <Icon className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold">{profile.name}</h3>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <Badge 
                              variant="outline" 
                              className="text-[10px]"
                              style={{ borderColor: templateConfig.color, color: templateConfig.color }}
                            >
                              {profile.template}
                            </Badge>
                            {profile.is_system && (
                              <Badge variant="secondary" className="text-[10px]">System</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      {!profile.is_system && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive"
                          onClick={() => setDeleteConfirm({ open: true, profile })}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                      {profile.description || 'No description provided'}
                    </p>
                    
                    <div className="flex items-center justify-between pt-3 border-t">
                      <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                        <Users className="h-4 w-4" />
                        <span>{profile.user_count} users</span>
                      </div>
                      <div className="flex gap-1">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleOpenUserDialog(profile)}
                        >
                          <Users className="h-3.5 w-3.5 mr-1.5" />
                          Users
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setProfileDialog({ open: true, profile })}
                        >
                          <Edit className="h-3.5 w-3.5 mr-1.5" />
                          Edit
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            {filteredProfiles.length === 0 && (
              <Card className="md:col-span-2 lg:col-span-3">
                <CardContent className="p-8 text-center text-muted-foreground">
                  <Shield className="h-12 w-12 mx-auto mb-4 opacity-20" />
                  <p>No profiles found</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* User Monitoring Tab */}
        <TabsContent value="user-monitoring" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <CardTitle className="text-lg">User Access Monitoring</CardTitle>
                  <CardDescription>
                    Current user status, tenant context, login/logout timing, and recent activity.
                  </CardDescription>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center text-xs">
                  <div className="rounded-lg border bg-muted/30 px-3 py-2">
                    <p className="font-semibold text-foreground">{monitoringMeta.summary?.online_now || 0}</p>
                    <p className="text-muted-foreground">Online Now</p>
                  </div>
                  <div className="rounded-lg border bg-muted/30 px-3 py-2">
                    <p className="font-semibold text-foreground">{monitoringMeta.summary?.logged_this_week || 0}</p>
                    <p className="text-muted-foreground">This Week</p>
                  </div>
                  <div className="rounded-lg border bg-muted/30 px-3 py-2">
                    <p className="font-semibold text-foreground">{monitoringMeta.summary?.logged_this_month || 0}</p>
                    <p className="text-muted-foreground">This Month</p>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3 lg:grid-cols-6">
                <div className="lg:col-span-2">
                  <Label className="text-xs">User</Label>
                  <Input
                    value={monitoringFilters.user}
                    onChange={(e) => updateMonitoringFilter('user', e.target.value)}
                    placeholder="Name or email"
                    data-testid="monitoring-user-filter"
                  />
                </div>
                <div className="lg:col-span-2">
                  <Label className="text-xs">Empresa</Label>
                  <Input
                    value={monitoringFilters.empresa}
                    onChange={(e) => updateMonitoringFilter('empresa', e.target.value)}
                    placeholder="Company or business"
                    data-testid="monitoring-empresa-filter"
                  />
                </div>
                <div>
                  <Label className="text-xs">Status</Label>
                  <Select value={monitoringFilters.status} onValueChange={(value) => updateMonitoringFilter('status', value)}>
                    <SelectTrigger data-testid="monitoring-status-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="online">Online</SelectItem>
                      <SelectItem value="recently_active">Recently Active</SelectItem>
                      <SelectItem value="offline">Offline</SelectItem>
                      <SelectItem value="no_session">No Session</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Profile</Label>
                  <Select value={monitoringFilters.profile} onValueChange={(value) => updateMonitoringFilter('profile', value)}>
                    <SelectTrigger data-testid="monitoring-profile-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Profiles</SelectItem>
                      {sortedProfiles.map(profile => (
                        <SelectItem key={profile.id} value={String(profile.id)}>
                          {profile.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">From</Label>
                  <Input
                    type="date"
                    value={monitoringFilters.dateFrom}
                    onChange={(e) => updateMonitoringFilter('dateFrom', e.target.value)}
                    data-testid="monitoring-date-from"
                  />
                </div>
                <div>
                  <Label className="text-xs">To</Label>
                  <Input
                    type="date"
                    value={monitoringFilters.dateTo}
                    onChange={(e) => updateMonitoringFilter('dateTo', e.target.value)}
                    data-testid="monitoring-date-to"
                  />
                </div>
                <div className="flex items-end gap-2 lg:col-span-4">
                  <Button
                    onClick={() => fetchUserMonitoring(1)}
                    disabled={monitoringLoading || dateFilterNeedsScope}
                    data-testid="monitoring-search-btn"
                  >
                    <Search className="h-4 w-4 mr-2" />
                    Search
                  </Button>
                  <Button variant="outline" onClick={resetMonitoringFilters} disabled={monitoringLoading}>
                    Reset
                  </Button>
                  <Button variant="outline" onClick={() => fetchUserMonitoring(monitoringMeta.page)} disabled={monitoringLoading}>
                    <RefreshCw className={cn('h-4 w-4 mr-2', monitoringLoading && 'animate-spin')} />
                    Refresh
                  </Button>
                </div>
              </div>

              {dateFilterNeedsScope && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
                  Date range requires User, Empresa, Status, or Profile filter before search.
                </div>
              )}
              {monitoringError && !dateFilterNeedsScope && (
                <div className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200">
                  {monitoringError}
                </div>
              )}

              <div className="overflow-auto rounded-lg border">
                <table className="w-full min-w-[1120px] text-sm">
                  <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="px-3 py-3 text-left">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            aria-label="Select or unselect all visible user monitoring rows"
                            checked={allMonitoringRowsSelected ? true : (someMonitoringRowsSelected ? 'indeterminate' : false)}
                            onCheckedChange={(checked) => toggleAllMonitoringRows(checked === true)}
                            disabled={monitoringRows.length === 0}
                            className="h-4 w-4"
                          />
                          <SortableMonitoringHeader label="Status" sortKey="status" />
                        </div>
                      </th>
                      <th className="px-3 py-3 text-left"><SortableMonitoringHeader label="User" sortKey="user" /></th>
                      <th className="px-3 py-3 text-left"><SortableMonitoringHeader label="Empresa" sortKey="empresa" /></th>
                      <th className="px-3 py-3 text-left"><SortableMonitoringHeader label="Profile" sortKey="profile" /></th>
                      <th className="px-3 py-3 text-left">Current Page</th>
                      <th className="px-3 py-3 text-left"><SortableMonitoringHeader label="Login Time" sortKey="login_time" /></th>
                      <th className="px-3 py-3 text-left"><SortableMonitoringHeader label="Logout Time" sortKey="logout_time" /></th>
                      <th id="last-activity" className="px-3 py-3 text-left"><SortableMonitoringHeader label="Last Activity" sortKey="last_activity_at" /></th>
                      <th className="px-3 py-3 text-left"><SortableMonitoringHeader label="Duration" sortKey="duration" /></th>
                      <th className="px-3 py-3 text-left"><SortableMonitoringHeader label="IP / Device" sortKey="ip_device" /></th>
                    </tr>
                  </thead>
                  <tbody className="text-xs">
                    {monitoringLoading ? (
                      <tr>
                        <td colSpan={10} className="px-3 py-8 text-center text-muted-foreground">
                          <RefreshCw className="mx-auto mb-2 h-5 w-5 animate-spin" />
                          Loading user access records...
                        </td>
                      </tr>
                    ) : monitoringRows.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="px-3 py-8 text-center text-muted-foreground">
                          <Shield className="mx-auto mb-3 h-10 w-10 opacity-20" />
                          No user access records found
                        </td>
                      </tr>
                    ) : (
                      sortedMonitoringRows.map(row => (
                        <tr
                          key={row.user_id}
                          tabIndex={0}
                          aria-selected={selectedMonitoringUserIds.includes(row.user_id)}
                          onClick={() => toggleMonitoringRowSelection(row.user_id)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              toggleMonitoringRowSelection(row.user_id);
                            }
                          }}
                          className={cn(
                            'border-t cursor-pointer transition-colors hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
                            getMonitoringRowStatusClass(row),
                            selectedMonitoringUserIds.includes(row.user_id) && 'bg-primary/5 ring-1 ring-primary/30'
                          )}
                        >
                          <td className="px-3 py-3">
                            <Badge
                              variant="outline"
                              className={cn(
                                row.status_key === 'online'
                                  ? 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30'
                                  : row.status_key === 'recently_active'
                                    ? 'border-amber-300 bg-amber-50 text-amber-700 dark:bg-amber-950/30'
                                  : 'border-slate-300 bg-slate-50 text-slate-600 dark:bg-slate-900/40'
                              )}
                            >
                              {row.status}
                            </Badge>
                          </td>
                          <td className="px-3 py-3">
                            <p className="font-medium text-foreground">{row.user_name}</p>
                            <p className="text-xs text-muted-foreground">
                              {row.username ? `@${row.username} · ` : ''}{row.email}
                            </p>
                          </td>
                          <td className="px-3 py-3">
                            <p className="font-medium text-foreground">{row.empresa}</p>
                            <p className="text-xs text-muted-foreground">{row.business || '-'}</p>
                          </td>
                          <td className="px-3 py-3">
                            <div className="flex flex-wrap gap-1">
                              {(row.profiles || []).length > 0 ? row.profiles.map(profileName => (
                                <Badge key={profileName} variant="secondary" className="text-[11px]">
                                  {profileName}
                                </Badge>
                              )) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-3">
                            <p className="font-medium text-foreground">{row.current_module || row.current_page || '-'}</p>
                            <p className="max-w-[220px] truncate text-xs text-muted-foreground" title={row.current_path || ''}>
                              {row.current_path || (row.online_connections ? `${row.online_connections} connection(s)` : '-')}
                            </p>
                          </td>
                          <td className="px-3 py-3">{formatMonitoringDate(row.login_time)}</td>
                          <td className="px-3 py-3">{formatMonitoringDate(row.logout_time)}</td>
                          <td className="px-3 py-3">{formatMonitoringDate(row.last_activity_at)}</td>
                          <td className="px-3 py-3">{row.session_duration || '-'}</td>
                          <td className="px-3 py-3">
                            <p>{row.ip_address || '-'}</p>
                            <p className="max-w-[220px] truncate text-xs text-muted-foreground" title={row.device || ''}>
                              {row.device || '-'}
                            </p>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>
                  Showing {monitoringRows.length} of {monitoringMeta.total} records
                  {selectedMonitoringCount > 0 ? ` · ${selectedMonitoringCount} selected` : ''}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={monitoringMeta.page <= 1 || monitoringLoading}
                    onClick={() => fetchUserMonitoring(monitoringMeta.page - 1)}
                  >
                    Previous
                  </Button>
                  <span>Page {monitoringMeta.page} of {totalMonitoringPages}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={monitoringMeta.page >= totalMonitoringPages || monitoringLoading}
                    onClick={() => fetchUserMonitoring(monitoringMeta.page + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <ProfileDialog
        profile={profileDialog.profile}
        isOpen={profileDialog.open}
        onClose={() => setProfileDialog({ open: false, profile: null })}
        onSave={handleSaveProfile}
      />

      <UserAssignmentDialog
        profile={userDialog.profile}
        users={users}
        isOpen={userDialog.open}
        onClose={() => setUserDialog({ open: false, profile: null })}
        onAssign={handleAssignUser}
        onUnassign={handleUnassignUser}
        onEditUser={(user) => setEditUserDialog({ open: true, user })}
        onDeleteUser={handleDeactivateUser}
        onNewUser={() => setCreateUserDialogOpen(true)}
      />

      <CreateUserDialog
        isOpen={createUserDialogOpen}
        onClose={() => setCreateUserDialogOpen(false)}
        onCreate={handleCreateUser}
        clients={clients}
        profiles={profiles}
        token={token}
        grantableMeta={grantableMeta}
      />

      <EditUserDialog
        user={editUserDialog.user}
        isOpen={editUserDialog.open}
        onClose={() => setEditUserDialog({ open: false, user: null })}
        onSave={handleUpdateUser}
        grantableMeta={grantableMeta}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirm.open} onOpenChange={() => setDeleteConfirm({ open: false, profile: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Profile</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteConfirm.profile?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm({ open: false, profile: null })}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteProfile}>
              Delete Profile
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
